import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { useLocale } from '@/lib/next-shim'
import { t } from '@lingui/core/macro'
import { Link } from '@/lib/next-shim'

import { Link as LinkIcon, Calendar, Clock, MapPin, Trash2, FileText, AlertTriangle } from 'react-feather'
import { OrderStatus } from '@/core/graphql/types'

import DoctorIcon from '@/assets/svg/Doctor'
import { CardActions, CardHeader } from '@/components/ui/cards/layout'
import { Button } from '@/components/ui/button'
import { IconText } from '@/components/ui/IconText'
import { MeetingPlaceBadge } from '@/components/ui/MeetingPlaceBadge'
import { cn } from '@/lib/utils'

import type { Order } from '../model/types'
import { getMeetingPlaceDescription } from '../lib/orderStatus'
import { OrderStatusBadge } from './OrderStatusBadge'

export type OrderCardProps = {
  className?: string
  order: Order
  onDelete?: (orderId: string) => void
}

export function OrderCard({ className, order, onDelete }: OrderCardProps) {
  const lang = useLocale()

  const isDraft = order.status === OrderStatus.Draft
  const hasFiles = order.files && order.files.length > 0

  return (
    <div
      data-testid="account-order-card"
      className={cn(
        `min-w-0 w-full drop-shadow-lg bg-white gap-1 flex flex-col rounded-lg hover:border-opacity-100 hover:shadow-lg transition-all duration-300 h-full overflow-hidden`,
        isDraft ? 'border border-dashed border-gray-300' : '',
        className
      )}
    >
      <CardHeader className="p-4">
        <div className="w-5/6 min-w-0 overflow-hidden flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="truncate text-blue-900 capitalize font-bold text-md">{order.meetingLink?.name || 'N/A'}</p>
          </div>
          <p className="text-zinc-500 text-sm md:text-md capitalize">{order.speciality}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <MeetingPlaceBadge placeId={order.meetingPlace?.placeId} />
            <OrderStatusBadge status={order.status} />
          </div>
        </div>
        <div className="w-1/6 pb-1 rounded-full overflow-hidden flex flex-row justify-center h-12">
          {!isDraft && order.acceptedBy ? (
            <DoctorIcon className="w-full h-full object-cover" isFemale={order.acceptedBy?.userInfo.gender === 'F'} />
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </div>
      </CardHeader>

      <div className="infos grid grid-cols-4 w-full flex-grow gap-x-4 gap-y-2 px-4 py-3">
        <div className="col-span-4 md:col-span-2 min-w-0 flex flex-col items-start gap-2">
          <IconText
            icon={<Calendar size={15} />}
            text={dayjs(order.startDate).locale(lang).format('ddd D MMM')}
            iconClassName="text-zinc-500 shrink-0"
          />
          <IconText icon={<Clock size={15} />} text={dayjs(order.startDate).locale(lang).format('HH[h]mm')} iconClassName="text-zinc-500 shrink-0" />
          {order.meetingLink?.isPrioritized && (
            <IconText
              icon={<AlertTriangle size={15} />}
              text={t`${order.files?.length || 0} file(s)`}
              className="rounded-md bg-orange-50 px-2 py-1 font-medium text-orange-500"
              iconClassName="text-orange-500 shrink-0"
            />
          )}
        </div>
        <div className="col-span-4 md:col-span-2 min-w-0 flex flex-col items-start gap-2">
          <IconText
            className="max-w-full"
            icon={<MapPin size={15} />}
            text={getMeetingPlaceDescription(order.meetingPlace)}
            iconClassName="text-zinc-500 shrink-0"
            textClassName="truncate"
          />
          {hasFiles && (
            <IconText icon={<FileText size={15} />} text={t`${order.files?.length || 0} file(s)`} iconClassName="text-zinc-500 shrink-0" />
          )}
          {order.meetingLink?.description && <p className="text-sm text-gray-500 line-clamp-2">{order.meetingLink.description}</p>}
        </div>
      </div>

      <div className="mt-auto px-4">
        <CardActions>
          <div className="flex flex-row justify-center gap-2 w-full">
            {isDraft && onDelete && (
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-red-50 hover:text-red-500" onClick={() => onDelete(order._id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Link
              className="flex-1 bg-primary hover:bg-blue-700 py-2 px-4 rounded-full flex flex-row justify-center"
              href={`/account/orders/${order.referenceId}`}
            >
              <LinkIcon className="h-5 w-5 text-white font-bold" />
            </Link>
          </div>
        </CardActions>
      </div>
    </div>
  )
}
